import React, { useState, useEffect } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NotesIcon from "@mui/icons-material/Notes";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BadgeIcon from "@mui/icons-material/Badge";
import CategoryIcon from "@mui/icons-material/Category";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptIcon from "@mui/icons-material/Receipt";
import GavelIcon from "@mui/icons-material/Gavel";
import LanguageIcon from "@mui/icons-material/Language";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import axios from "axios";
import moment from "moment";
import TaxOfficeSelect from "./TaxOfficeSelect";
import { formatTaxOfficeLabel, isGermanyCountry } from "../utils/taxOfficeUtils";

const steps = [
  "Customer Information",
  "Add Entities",
  "Overview & Confirmation",
];

const USE_DUMMY_ONBOARDING_DATA = false;

const createEmptyFormData = () => ({
  customerName: "",
  contactPerson: "",
  email: "",
  phone: "",
  address: "",
  postalCode: "",
  city: "",
  countryId: "",
  notes: "",
  entities: [],
});

// Keep for future testing. Toggle USE_DUMMY_ONBOARDING_DATA when needed.
const createDummyFormData = () => ({
  customerName: "Acme Corporation",
  contactPerson: "John Doe",
  email: "john.doe@acme.com",
  phone: "+31 20 123 4567",
  address: "Main Street 123",
  postalCode: "1000 AA",
  city: "Amsterdam",
  countryId: 1,
  notes: "This is a test customer for onboarding purposes. Please review all information carefully before proceeding.",
  entities: [
    {
      entityNumber: "ENT-001",
      companyName: "Acme Corporation B.V.",
      relationType: 5,
      companyAddress: "Business Park 456",
      houseNumber: "",
      companyPostalCode: "2000 BB",
      companyCity: "Rotterdam",
      companyProvince: "South Holland",
      companyCounty: "",
      companyMunicipality: "",
      postOffice: "",
      countryId: 1,
      coc: "12345678",
      vat: "NL123456789B01",
      steuerNummer: "",
      taxOfficeId: "",
      taxOffice: "Amsterdam",
      website: "https://www.acme-corp.com",
      email: "info@acme-corp.com",
      phonenumber: "+31 10 987 6543",
      startupDate: "2020-01-15",
    },
    {
      entityNumber: "ENT-002",
      companyName: "Acme International Ltd.",
      relationType: 5,
      companyAddress: "Industrial Avenue 789",
      houseNumber: "",
      companyPostalCode: "3000 CC",
      companyCity: "Utrecht",
      companyProvince: "Utrecht",
      companyCounty: "",
      companyMunicipality: "",
      postOffice: "",
      countryId: 1,
      coc: "87654321",
      vat: "NL987654321B02",
      steuerNummer: "",
      taxOfficeId: "",
      taxOffice: "Utrecht",
      website: "https://www.acme-international.com",
      email: "contact@acme-international.com",
      phonenumber: "+31 30 555 1234",
      startupDate: "2021-03-20",
    },
    {
      entityNumber: "ENT-003",
      companyName: "Acme Poland Sp. z o.o.",
      relationType: 5,
      companyAddress: "ul. Marszałkowska",
      houseNumber: "15",
      companyPostalCode: "00-624",
      companyCity: "Warsaw",
      companyProvince: "Mazowieckie",
      companyCounty: "Warsaw",
      companyMunicipality: "Śródmieście",
      postOffice: "Warszawa 1",
      countryId: 17,
      coc: "PL1234567890",
      vat: "PL1234567890",
      steuerNummer: "",
      taxOfficeId: "",
      taxOffice: "Warsaw Tax Office",
      website: "https://www.acme-poland.pl",
      email: "biuro@acme-poland.pl",
      phonenumber: "+48 22 123 4567",
      startupDate: "2022-06-10",
    },
  ],
});

const createInitialFormData = () => {
  if (USE_DUMMY_ONBOARDING_DATA) {
    return createDummyFormData();
  }

  return createEmptyFormData();
};

const OnboardingCustomerWizard = ({ open, onClose, onComplete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeStep, setActiveStep] = useState(0);
  const [countries, setCountries] = useState([]);
  const [relationTypes, setRelationTypes] = useState([]);
  const [entityDialogOpen, setEntityDialogOpen] = useState(false);
  const [editingEntityIndex, setEditingEntityIndex] = useState(null);
  const [currentEntity, setCurrentEntity] = useState(null);
  const [entityDialogError, setEntityDialogError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [currentEntityIndex, setCurrentEntityIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [formData, setFormData] = useState(() => createInitialFormData());

  useEffect(() => {
    if (open) {
      // Fetch countries and relation types when dialog opens
      axios
        .get("/api/v1/countries")
        .then((response) => setCountries(response.data))
        .catch((error) => console.error("Error fetching countries:", error));

      axios
        .get("/api/v1/relationTypes")
        .then((response) => setRelationTypes(response.data))
        .catch((error) => console.error("Error fetching relation types:", error));
    }
  }, [open]);

  const isStepValid = (step) => {
    if (step === 0) {
      // Check if customer information is valid
      if (!formData.customerName || formData.customerName.trim() === "") {
        return false;
      }
      if (!formData.countryId) {
        return false;
      }
      if (!formData.email || formData.email.trim() === "") {
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        return false;
      }
    }
    return true;
  };

  const validateStep = (step) => {
    const errors = {};
    
    if (step === 0) {
      // Validate customer information
      if (!formData.customerName || formData.customerName.trim() === "") {
        errors.customerName = "Customer Name is required";
      }
      if (!formData.countryId) {
        errors.countryId = "Country is required";
      }
      if (!formData.email || formData.email.trim() === "") {
        errors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = "Please enter a valid email address";
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) {
      return;
    }
    // Clear validation errors when moving to next step
    setValidationErrors({});
    if (activeStep === steps.length - 1) {
      handleComplete();
    } else {
      setActiveStep((prevActiveStep) => {
        const nextStep = prevActiveStep + 1;
        // Reset entity index when entering overview step
        if (nextStep === 2) {
          setCurrentEntityIndex(0);
        }
        return nextStep;
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = async () => {
    // Validate required fields
    if (!formData.customerName || !formData.email || !formData.countryId) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Step 1: Create prospect
      const prospectData = {
        companyName: formData.customerName,
        countryId: formData.countryId,
        contactName: formData.contactPerson || null,
        email: formData.email || null,
        phone: formData.phone || null,
        address: formData.address || null,
        postalCode: formData.postalCode || null,
        city: formData.city || null,
      };

      const prospectResponse = await axios.post("/api/v1/prospects", prospectData);
      const prospectId = prospectResponse.data.id;

      // Step 2: Create all entities using the new endpoint that automatically links them to the prospect
      const entityIds = [];
      
      console.log(`Creating ${formData.entities.length} entities for prospect ${prospectId}`);
      
      for (let i = 0; i < formData.entities.length; i++) {
        const entity = formData.entities[i];
        try {
          console.log(`Creating entity ${i + 1}/${formData.entities.length}:`, entity.companyName);
          
          const entityPayload = {
            // Note: entityNumber and typeRelation are set server-side, but we include them for completeness
            entityNumber: entity.entityNumber,
            typeRelation: entity.relationType,
            name: entity.companyName,
            address: entity.companyAddress || "",
            houseNumber: isEntityPoland(entity) ? (entity.houseNumber || "") : "",
            postalCode: entity.companyPostalCode || "",
            city: entity.companyCity || "",
            province: entity.companyProvince || "",
            county: isEntityPoland(entity) ? (entity.companyCounty || "") : "",
            municipality: isEntityPoland(entity) ? (entity.companyMunicipality || "") : "",
            postOffice: isEntityPoland(entity) ? (entity.postOffice || "") : "",
            countryId: entity.countryId,
            coC: entity.coc || "",
            vat: entity.vat || "",
            steuerNummer: isGermanyCountry(entity.countryId, countries) ? entity.steuerNummer || "" : "",
            taxOfficeId: entity.taxOfficeId || null,
            taxOffice: entity.taxOffice || "",
            website: entity.website || "",
            generalEmailAddress: entity.email || "",
            generalPhoneNumber: entity.phonenumber || "",
            supplierId: 0,
            creationDate: moment().format("YYYY-MM-DDThh:mm:ss.SSS") + "Z",
            startupDate: entity.startupDate || null,
            endDate: null,
            modifiedDate: moment().format("YYYY-MM-DDThh:mm:ss.SSS") + "Z",
            isDisabled: true,
            entityStatusId: 0,
          };

          // Use the new endpoint that creates entity and links it to prospect automatically
          const entityResponse = await axios.post(`/api/v1/prospects/${prospectId}/create-prospect-entity`, entityPayload);
          console.log(`Entity ${i + 1} response:`, entityResponse.data);
          
          // The response should contain the entityId (from EntitiesReadDto)
          const entityId = entityResponse.data?.entityId || entityResponse.data?.id;
          if (entityId) {
            entityIds.push(entityId);
            console.log(`Entity ${i + 1} created successfully with ID: ${entityId}`);
          } else {
            console.error(`Entity ${i + 1} created but no entityId in response:`, entityResponse.data);
            console.error("Full response:", entityResponse);
          }
        } catch (error) {
          console.error(`Error creating entity ${i + 1} (${entity.companyName}):`, error);
          console.error("Error details:", error.response?.data || error.message);
          // Continue with other entities even if one fails
        }
      }
      
      console.log(`Created ${entityIds.length} out of ${formData.entities.length} entities`);

      // Step 3: Create prospect note if notes are provided
      if (formData.notes && formData.notes.trim()) {
        try {
          // Format current date as dd-MM-yyyy
          const currentDate = new Date();
          const day = String(currentDate.getDate()).padStart(2, '0');
          const month = String(currentDate.getMonth() + 1).padStart(2, '0');
          const year = currentDate.getFullYear();
          const formattedDate = `${day}-${month}-${year}`;
          
          const notePayload = {
            prospectId: prospectId,
            shortDescription: `Prospect Dossier created on ${formattedDate}`,
            noteContent: formData.notes.trim(),
          };

          console.log("Creating prospect note with payload:", notePayload);
          await axios.post("/api/v1/prospectnotes", notePayload);
          console.log("Prospect note created successfully");
        } catch (error) {
          console.error("Error creating prospect note:", error);
          console.error("Error response:", error.response?.data);
          // Don't fail the entire process if note creation fails
        }
      }

      // Pass the created prospect data to parent component
      onComplete({
        ...formData,
        prospectId: prospectId,
        entityIds: entityIds,
      });
      
      handleReset();
    } catch (error) {
      console.error("Error completing onboarding:", error);
      // You might want to show an error message to the user here
      alert("Error completing onboarding: " + (error.response?.data?.message || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setEntityDialogOpen(false);
    setEditingEntityIndex(null);
    setCurrentEntity(null);
    setValidationErrors({});
    setCurrentEntityIndex(0);
    setFormData(createInitialFormData());
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEntityChange = (index, field, value) => {
    setFormData((prev) => {
      const newEntities = [...prev.entities];
      newEntities[index] = {
        ...newEntities[index],
        [field]: value,
      };
      // If country changes, check if it's Poland (countryId === 17)
      if (field === "countryId") {
        newEntities[index].taxOfficeId = "";
        newEntities[index].taxOffice = "";
        if (value !== 17) {
          newEntities[index].houseNumber = "";
          newEntities[index].companyCounty = "";
          newEntities[index].companyMunicipality = "";
          newEntities[index].postOffice = "";
        }
        if (!isGermanyCountry(value, countries)) {
          newEntities[index].steuerNummer = "";
        }
      }
      return {
        ...prev,
        entities: newEntities,
      };
    });
  };

  const isEntityPoland = (entity) => {
    return entity.countryId === 17;
  };

  const handleAddEntity = () => {
    setCurrentEntity({
      entityNumber: "",
      companyName: "",
      relationType: 5, // Default to Prospect (id 5)
      companyAddress: "",
      houseNumber: "",
      companyPostalCode: "",
      companyCity: "",
      companyProvince: "",
      companyCounty: "",
      companyMunicipality: "",
      postOffice: "",
      countryId: "",
      coc: "",
      vat: "",
      steuerNummer: "",
      taxOfficeId: "",
      taxOffice: "",
      website: "",
      email: "",
      phonenumber: "",
      startupDate: "",
    });
    setEditingEntityIndex(null);
    setEntityDialogError("");
    setEntityDialogOpen(true);
  };

  const handleEditEntity = (index) => {
    setCurrentEntity({ ...formData.entities[index] });
    setEditingEntityIndex(index);
    setEntityDialogError("");
    setEntityDialogOpen(true);
  };

  const handleRemoveEntity = (index) => {
    setFormData((prev) => ({
      ...prev,
      entities: prev.entities.filter((_, i) => i !== index),
    }));
  };

  const handleSaveEntity = () => {
    if (isGermanyCountry(currentEntity?.countryId, countries) && !String(currentEntity?.steuerNummer ?? "").trim()) {
      setEntityDialogError("Steuernummer is required for German customers.");
      return;
    }

    setEntityDialogError("");
    if (editingEntityIndex !== null) {
      // Update existing entity
      setFormData((prev) => {
        const newEntities = [...prev.entities];
        newEntities[editingEntityIndex] = currentEntity;
        return {
          ...prev,
          entities: newEntities,
        };
      });
    } else {
      // Add new entity
      setFormData((prev) => ({
        ...prev,
        entities: [...prev.entities, currentEntity],
      }));
    }
    setEntityDialogOpen(false);
    setCurrentEntity(null);
    setEditingEntityIndex(null);
  };

  const handleEntityFormChange = (field, value, extra = {}) => {
    setCurrentEntity((prev) => {
      const updated = {
        ...prev,
        [field]: value,
        ...extra,
      };
      if (field === "countryId") {
        updated.taxOfficeId = "";
        updated.taxOffice = "";
        if (value !== 17) {
          updated.houseNumber = "";
          updated.companyCounty = "";
          updated.companyMunicipality = "";
          updated.postOffice = "";
        }
        if (!isGermanyCountry(value, countries)) {
          updated.steuerNummer = "";
        }
      }
      return updated;
    });
  };

  const isEntityPolandForm = () => {
    return currentEntity?.countryId === 17;
  };

  const isEntityGermanyForm = () => {
    return isGermanyCountry(currentEntity?.countryId, countries);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Customer Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Customer Name"
                  value={formData.customerName}
                  onChange={(e) => {
                    handleInputChange("customerName", e.target.value);
                    // Clear error when user starts typing
                    if (validationErrors.customerName) {
                      setValidationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.customerName;
                        return newErrors;
                      });
                    }
                  }}
                  required
                  error={!!validationErrors.customerName}
                  helperText={validationErrors.customerName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contactPerson}
                  onChange={(e) => handleInputChange("contactPerson", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    handleInputChange("email", e.target.value);
                    // Clear error when user starts typing
                    if (validationErrors.email) {
                      setValidationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.email;
                        return newErrors;
                      });
                    }
                  }}
                  required
                  error={!!validationErrors.email}
                  helperText={validationErrors.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange("postalCode", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => handleInputChange("city", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required error={!!validationErrors.countryId}>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={formData.countryId}
                    label="Country"
                    onChange={(e) => {
                      handleInputChange("countryId", e.target.value);
                      // Clear error when user selects a country
                      if (validationErrors.countryId) {
                        setValidationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.countryId;
                          return newErrors;
                        });
                      }
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.countryId} value={country.countryId}>
                        {country.countryCode}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.countryId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {validationErrors.countryId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        const entityColumns = [
          { field: "id", headerName: "ID", width: 50, hide: true },
          { field: "entityNumber", headerName: "Customer Number", flex: 1 },
          { field: "companyName", headerName: "Company Name", flex: 1.5 },
          {
            field: "relationType",
            headerName: "Relation Type",
            flex: 1,
            valueGetter: (params) => {
              const type = relationTypes.find((t) => t.relationTypeId === params.row.relationType);
              return type ? type.shortDescription : "";
            },
          },
          {
            field: "countryId",
            headerName: "Country",
            flex: 0.8,
            valueGetter: (params) => {
              const country = countries.find((c) => c.countryId === params.row.countryId);
              return country ? country.countryCode : "";
            },
          },
          { field: "email", headerName: "Email", flex: 1.2 },
          { field: "phonenumber", headerName: "Phone", flex: 1 },
          {
            field: "actions",
            headerName: "Actions",
            flex: 0.8,
            sortable: false,
            renderCell: (params) => {
              return (
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEditEntity(params.row.id)}
                    aria-label="Edit entity"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveEntity(params.row.id)}
                    aria-label="Delete entity"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            },
          },
        ];

        const entityRows = formData.entities.map((entity, index) => ({
          id: index,
          ...entity,
        }));

        return (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="h6">
                Entities ({formData.entities.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddEntity}
                size="small"
              >
                Add Entity
              </Button>
            </Box>
            <Box sx={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={entityRows}
                columns={entityColumns}
                pageSize={5}
                rowsPerPageOptions={[5, 10]}
                disableSelectionOnClick
                density="compact"
                sx={{
                  "& .MuiDataGrid-cell:hover": {
                    color: "primary.main",
                  },
                }}
              />
            </Box>
          </Box>
        );

      case 2:
        const getCountryName = (countryId) => {
          const country = countries.find((c) => c.countryId === countryId);
          return country ? `${country.countryCode}` : "";
        };

        const getRelationTypeName = (relationTypeId) => {
          const type = relationTypes.find((t) => t.relationTypeId === relationTypeId);
          return type ? type.shortDescription : "";
        };

        return (
          <Box sx={{ mt: { xs: 1, sm: 3 }, mb: { xs: 0, sm: 0 } }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 2, sm: 3 } }}>
              <CheckCircleIcon color="primary" sx={{ mr: 1, fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
              <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                Overview & Confirmation
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
              Please review all information before completing the onboarding process.
            </Typography>

            {/* Customer Information Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <PersonIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Customer Information</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <BusinessIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        Customer Name
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                      {formData.customerName || <em style={{ color: "#999" }}>Not provided</em>}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <PersonIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        Contact Person
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                      {formData.contactPerson || <em style={{ color: "#999" }}>Not provided</em>}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <EmailIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                      {formData.email || <em style={{ color: "#999" }}>Not provided</em>}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                      {formData.phone || <em style={{ color: "#999" }}>Not provided</em>}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <LocationOnIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        Address
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                      {formData.address ? (
                        <>
                          {formData.address}
                          {formData.postalCode && `, ${formData.postalCode}`}
                          {formData.city && ` ${formData.city}`}
                          {formData.countryId && `, ${getCountryName(formData.countryId)}`}
                        </>
                      ) : (
                        <em style={{ color: "#999" }}>Not provided</em>
                      )}
                    </Typography>
                  </Grid>
                  {formData.notes && (
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                        <NotesIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary">
                          Notes
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          maxHeight: "80px",
                          overflowY: "auto",
                          overflowX: "hidden",
                          pr: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {formData.notes}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Entities Card */}
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <BusinessIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Entities ({formData.entities.length})
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {formData.entities.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                    No entities added
                  </Typography>
                ) : (
                  <Box>
                    {/* Pagination Controls */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                      <IconButton
                        onClick={() => setCurrentEntityIndex((prev) => Math.max(0, prev - 1))}
                        disabled={currentEntityIndex === 0}
                        aria-label="Previous entity"
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      <Typography variant="body2" color="text.secondary">
                        Entity {currentEntityIndex + 1} of {formData.entities.length}
                      </Typography>
                      <IconButton
                        onClick={() => setCurrentEntityIndex((prev) => Math.min(formData.entities.length - 1, prev + 1))}
                        disabled={currentEntityIndex === formData.entities.length - 1}
                        aria-label="Next entity"
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>

                    {/* Current Entity Display */}
                    {(() => {
                      const entity = formData.entities[currentEntityIndex];
                      
                      const minSwipeDistance = 50;

                      const onTouchStart = (e) => {
                        setTouchEnd(null);
                        setTouchStart(e.targetTouches[0].clientX);
                      };

                      const onTouchMove = (e) => {
                        setTouchEnd(e.targetTouches[0].clientX);
                      };

                      const onTouchEnd = () => {
                        if (!touchStart || !touchEnd) return;
                        
                        const distance = touchStart - touchEnd;
                        const isLeftSwipe = distance > minSwipeDistance;
                        const isRightSwipe = distance < -minSwipeDistance;

                        if (isLeftSwipe && currentEntityIndex < formData.entities.length - 1) {
                          setCurrentEntityIndex((prev) => prev + 1);
                        }
                        if (isRightSwipe && currentEntityIndex > 0) {
                          setCurrentEntityIndex((prev) => prev - 1);
                        }
                      };

                      return (
                        <Box
                          key={currentEntityIndex}
                          onTouchStart={onTouchStart}
                          onTouchMove={onTouchMove}
                          onTouchEnd={onTouchEnd}
                          sx={{
                            touchAction: "pan-y",
                            userSelect: "none",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {entity.companyName || "Unnamed Entity"}
                            </Typography>
                          </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <BadgeIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                Customer Number
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              {entity.entityNumber || <em style={{ color: "#999" }}>Not provided</em>}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <CategoryIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                Relation Type
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              {getRelationTypeName(entity.relationType) || <em style={{ color: "#999" }}>Not provided</em>}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <EmailIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                Email
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              {entity.email || <em style={{ color: "#999" }}>Not provided</em>}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                Phone
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              {entity.phonenumber || <em style={{ color: "#999" }}>Not provided</em>}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={8}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <LocationOnIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                Address
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              {isEntityPoland(entity) ? (
                                <>
                                  {entity.companyAddress || ""}
                                  {entity.houseNumber && ` ${entity.houseNumber}`}
                                  {entity.companyPostalCode && `, ${entity.companyPostalCode}`}
                                  {entity.companyCity && ` ${entity.companyCity}`}
                                  {entity.companyProvince && `, ${entity.companyProvince}`}
                                  {entity.companyCounty && `, ${entity.companyCounty}`}
                                  {entity.companyMunicipality && `, ${entity.companyMunicipality}`}
                                  {entity.postOffice && ` (Post Office: ${entity.postOffice})`}
                                  {entity.countryId && `, ${getCountryName(entity.countryId)}`}
                                </>
                              ) : (
                                <>
                                  {entity.companyAddress || ""}
                                  {entity.companyPostalCode && `, ${entity.companyPostalCode}`}
                                  {entity.companyCity && ` ${entity.companyCity}`}
                                  {entity.companyProvince && `, ${entity.companyProvince}`}
                                  {entity.countryId && `, ${getCountryName(entity.countryId)}`}
                                </>
                              )}
                              {!entity.companyAddress && !entity.companyPostalCode && !entity.companyCity && (
                                <em style={{ color: "#999" }}>Not provided</em>
                              )}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <AccountBalanceIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                Chamber of Commerce
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              {entity.coc || <em style={{ color: "#999" }}>Not provided</em>}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <ReceiptIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                VAT
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              {entity.vat || <em style={{ color: "#999" }}>Not provided</em>}
                            </Typography>
                          </Grid>
                          {isGermanyCountry(entity.countryId, countries) ? (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                <ReceiptIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                <Typography variant="caption" color="text.secondary">
                                  Steuernummer
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {entity.steuerNummer || <em style={{ color: "#999" }}>Not provided</em>}
                              </Typography>
                            </Grid>
                          ) : null}
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <GavelIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                Tax Office
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              {entity.taxOffice || <em style={{ color: "#999" }}>Not provided</em>}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <LanguageIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                Website
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              {entity.website ? (
                                <a href={entity.website} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>
                                  {entity.website}
                                </a>
                              ) : (
                                <em style={{ color: "#999" }}>Not provided</em>
                              )}
                            </Typography>
                          </Grid>
                          {entity.startupDate && (
                            <Grid item xs={12} md={4}>
                              <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                <Typography variant="caption" color="text.secondary">
                                  Start Date
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {(() => {
                                  try {
                                    const date = new Date(entity.startupDate);
                                    return isNaN(date.getTime()) ? entity.startupDate : date.toLocaleDateString();
                                  } catch {
                                    return entity.startupDate;
                                  }
                                })()}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                        </Box>
                      );
                    })()}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: { xs: "95vh", sm: "900px" },
          height: { xs: "95vh", sm: "auto" },
          margin: { xs: 1, sm: 2 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
        <Typography variant="h5" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
          Onboarding Customer
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
          Add a new prospect with customer information and entities
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          flex: 1,
          px: { xs: 2, sm: 3 },
          pt: { xs: 1, sm: 2 },
          pb: { xs: 1, sm: 1 },
          "&.MuiDialogContent-root": {
            paddingBottom: { xs: 1, sm: 1 },
          },
        }}
      >
        <Stepper 
          activeStep={activeStep} 
          orientation={isMobile ? "vertical" : "horizontal"}
          sx={{ 
            mb: { xs: 2, sm: 4 }, 
            mt: { xs: 1, sm: 2 },
            "& .MuiStepLabel-label": {
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            },
          }}
        >
          {steps.map((label, index) => {
            const isClickable = index <= activeStep || (index === activeStep + 1 && isStepValid(activeStep));
            return (
              <Step key={label}>
                <StepLabel
                  onClick={() => {
                    // Only allow navigation to completed steps or the next step
                    if (isClickable) {
                      if (index === activeStep + 1) {
                        // Validate current step before moving to next
                        if (!validateStep(activeStep)) {
                          return;
                        }
                      }
                      setActiveStep(index);
                      setValidationErrors({});
                      // Reset entity index when entering overview step
                      if (index === 2) {
                        setCurrentEntityIndex(0);
                      }
                    }
                  }}
                  sx={{
                    cursor: isClickable ? "pointer" : "default",
                    "&:hover": {
                      "& .MuiStepLabel-label": {
                        color: isClickable ? "primary.main" : "inherit",
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions 
        sx={{ 
          px: { xs: 2, sm: 3 }, 
          pt: { xs: 1, sm: 2 },
          pb: { xs: 1, sm: 2 }, 
          flexDirection: { xs: "column-reverse", sm: "row" }, 
          gap: { xs: 1, sm: 0 },
          flexShrink: 0,
        }}
      >
        <Button 
          onClick={handleClose} 
          color="inherit"
          fullWidth={isMobile}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Cancel
        </Button>
        <Box sx={{ flex: { xs: "0 0 auto", sm: "1 1 auto" } }} />
        <Button 
          disabled={activeStep === 0 || isSubmitting} 
          onClick={handleBack} 
          color="inherit"
          fullWidth={isMobile}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          fullWidth={isMobile}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {isSubmitting
            ? "Processing..."
            : activeStep === steps.length - 1
            ? "Complete"
            : "Next"}
        </Button>
      </DialogActions>

      {/* Entity Form Dialog */}
      <Dialog
        open={entityDialogOpen}
        onClose={() => {
          setEntityDialogOpen(false);
          setCurrentEntity(null);
          setEditingEntityIndex(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {editingEntityIndex !== null ? "Edit Entity" : "Add New Entity"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {currentEntity && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item md={4} xs={12}>
                  <TextField
                    fullWidth
                    label="Customer Number"
                    value={currentEntity.entityNumber}
                    onChange={(e) => handleEntityFormChange("entityNumber", e.target.value)}
                    required
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={currentEntity.companyName}
                    onChange={(e) => handleEntityFormChange("companyName", e.target.value)}
                    required
                  />
                </Grid>
                <Grid item md={2} xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Relation Type</InputLabel>
                    <Select
                      value={currentEntity.relationType}
                      label="Relation Type"
                      onChange={(e) => handleEntityFormChange("relationType", e.target.value)}
                      disabled
                    >
                      {relationTypes.map((type) => (
                        <MenuItem key={type.relationTypeId} value={type.relationTypeId}>
                          {type.shortDescription}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {isEntityPolandForm() ? (
                <>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item md={4} xs={12}>
                      <TextField
                        fullWidth
                        label="Street"
                        value={currentEntity.companyAddress}
                        onChange={(e) => handleEntityFormChange("companyAddress", e.target.value)}
                      />
                    </Grid>
                    <Grid item md={2} xs={12}>
                      <TextField
                        fullWidth
                        label="House Number"
                        value={currentEntity.houseNumber}
                        onChange={(e) => handleEntityFormChange("houseNumber", e.target.value)}
                      />
                    </Grid>
                    <Grid item md={2} xs={12}>
                      <TextField
                        fullWidth
                        label="Postal Code"
                        value={currentEntity.companyPostalCode}
                        onChange={(e) => handleEntityFormChange("companyPostalCode", e.target.value)}
                      />
                    </Grid>
                    <Grid item md={4} xs={12}>
                      <TextField
                        fullWidth
                        label="City"
                        value={currentEntity.companyCity}
                        onChange={(e) => handleEntityFormChange("companyCity", e.target.value)}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item md={3} xs={12}>
                      <TextField
                        fullWidth
                        label="Province"
                        value={currentEntity.companyProvince}
                        onChange={(e) => handleEntityFormChange("companyProvince", e.target.value)}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <TextField
                        fullWidth
                        label="County"
                        value={currentEntity.companyCounty}
                        onChange={(e) => handleEntityFormChange("companyCounty", e.target.value)}
                      />
                    </Grid>
                    <Grid item md={4} xs={12}>
                      <TextField
                        fullWidth
                        label="Municipality"
                        value={currentEntity.companyMunicipality}
                        onChange={(e) => handleEntityFormChange("companyMunicipality", e.target.value)}
                      />
                    </Grid>
                    <Grid item md={2} xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Country</InputLabel>
                        <Select
                          value={currentEntity.countryId}
                          label="Country"
                          onChange={(e) => handleEntityFormChange("countryId", e.target.value)}
                        >
                          {countries.map((country) => (
                            <MenuItem key={country.countryId} value={country.countryId}>
                              {country.countryCode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item md={8} xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        value={currentEntity.companyAddress}
                        onChange={(e) => handleEntityFormChange("companyAddress", e.target.value)}
                      />
                    </Grid>
                    <Grid item md={4} xs={12}>
                      <TextField
                        fullWidth
                        label="Postal Code"
                        value={currentEntity.companyPostalCode}
                        onChange={(e) => handleEntityFormChange("companyPostalCode", e.target.value)}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item md={5} xs={12}>
                      <TextField
                        fullWidth
                        label="City"
                        value={currentEntity.companyCity}
                        onChange={(e) => handleEntityFormChange("companyCity", e.target.value)}
                      />
                    </Grid>
                    <Grid item md={5} xs={12}>
                      <TextField
                        fullWidth
                        label="Province"
                        value={currentEntity.companyProvince}
                        onChange={(e) => handleEntityFormChange("companyProvince", e.target.value)}
                      />
                    </Grid>
                    <Grid item md={2} xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Country</InputLabel>
                        <Select
                          value={currentEntity.countryId}
                          label="Country"
                          onChange={(e) => handleEntityFormChange("countryId", e.target.value)}
                        >
                          {countries.map((country) => (
                            <MenuItem key={country.countryId} value={country.countryId}>
                              {country.countryCode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </>
              )}
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="General Email Address"
                    type="email"
                    value={currentEntity.email}
                    onChange={(e) => handleEntityFormChange("email", e.target.value)}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="General Phone Number"
                    value={currentEntity.phonenumber}
                    onChange={(e) => handleEntityFormChange("phonenumber", e.target.value)}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item md={isEntityGermanyForm() ? 4 : 6} xs={12}>
                  <TextField
                    fullWidth
                    label="Chamber of Commerce"
                    value={currentEntity.coc}
                    onChange={(e) => handleEntityFormChange("coc", e.target.value)}
                  />
                </Grid>
                <Grid item md={isEntityGermanyForm() ? 4 : 6} xs={12}>
                  <TextField
                    fullWidth
                    label="VAT"
                    value={currentEntity.vat}
                    onChange={(e) => handleEntityFormChange("vat", e.target.value)}
                  />
                </Grid>
                {isEntityGermanyForm() ? (
                  <Grid item md={4} xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Steuernummer"
                      value={currentEntity.steuerNummer}
                      onChange={(e) => {
                        handleEntityFormChange("steuerNummer", e.target.value);
                        setEntityDialogError("");
                      }}
                      error={Boolean(entityDialogError)}
                      helperText={entityDialogError}
                      inputProps={{ "aria-label": "Steuernummer" }}
                    />
                  </Grid>
                ) : null}
              </Grid>
              {isEntityPolandForm() ? (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item md={6} xs={12}>
                    <TaxOfficeSelect
                      value={currentEntity.taxOfficeId}
                      countryId={currentEntity.countryId}
                      onChange={(event) =>
                        handleEntityFormChange("taxOfficeId", event.target.value, {
                          taxOffice: event.option ? formatTaxOfficeLabel(event.option) : "",
                        })
                      }
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Post Office"
                      value={currentEntity.postOffice}
                      onChange={(e) => handleEntityFormChange("postOffice", e.target.value)}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12}>
                    <TaxOfficeSelect
                      value={currentEntity.taxOfficeId}
                      countryId={currentEntity.countryId}
                      onChange={(event) =>
                        handleEntityFormChange("taxOfficeId", event.target.value, {
                          taxOffice: event.option ? formatTaxOfficeLabel(event.option) : "",
                        })
                      }
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={currentEntity.website}
                    onChange={(e) => handleEntityFormChange("website", e.target.value)}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={currentEntity.startupDate}
                    onChange={(e) => handleEntityFormChange("startupDate", e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEntityDialogOpen(false);
              setCurrentEntity(null);
              setEditingEntityIndex(null);
              setEntityDialogError("");
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button onClick={handleSaveEntity} variant="contained" color="primary">
            {editingEntityIndex !== null ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default OnboardingCustomerWizard;

